import { Download, ShowChart } from "@mui/icons-material";
import { Box, IconButton, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { ChartsAxisContentProps } from "@mui/x-charts";
import { CommonOverlayProps } from "@mui/x-charts/ChartsOverlay/ChartsOverlay";
import { LineChart } from "@mui/x-charts/LineChart";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FlowTestMeasurementGroupResponse } from "../../../types/model/flowTestMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import { exportSvg, sanitizeFilename } from "../../../utils";

dayjs.extend(localizedFormat);

interface FlowTestMeasurementsChartProps {
  measurementsData: FlowTestMeasurementGroupResponse[];
  variables: VariableResponse[];
  isChartMaximized: boolean;
  flowTestId: string;
  selectedVariableId: number | undefined;
  xAxisType: "measuredAt" | "testingTime";
}

interface ChartDataPoint {
  id: number;
  value: number;
  measuredAt: Date | null;
  testingTime: number | null | undefined;
  flag: string | null | undefined;
  notes: string | null | undefined;
  [key: string]: any;
}

interface MeasurementAxisContentProps extends ChartsAxisContentProps {
  axisValue: number | Date | string | null;
  filteredData?: ChartDataPoint[];
  variableUnit?: string;
  xAxisType?: "measuredAt" | "testingTime";
}

interface NoDataOverlayProps extends CommonOverlayProps {
  message?: string;
}

function NoDataOverlay({ message }: Readonly<NoDataOverlayProps>) {
  return (
    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 14 }}>
      {message}
    </text>
  );
}

function MeasurementAxisContent({ axisValue, filteredData, variableUnit, xAxisType = "measuredAt" }: Readonly<MeasurementAxisContentProps>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  
  if (!filteredData || axisValue === null) return null;

  let dataPoint: ChartDataPoint | undefined;

  if (xAxisType === "measuredAt") {
    let axisTime: number;
    if (axisValue instanceof Date) {
      axisTime = axisValue.getTime();
    } else if (typeof axisValue === "number") {
      axisTime = axisValue;
    } else {
      axisTime = dayjs(axisValue).valueOf();
    }
    dataPoint = filteredData.find(d => d.measuredAt?.getTime() === axisTime);
  } else {
    dataPoint = filteredData.find(d => d.testingTime === axisValue);
  }

  if (!dataPoint) return null;

  return (
    <Paper sx={{ p: 1.5, border: `1px solid ${theme.palette.divider}`, minWidth: 200, boxShadow: theme.shadows[3] }}>
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {dataPoint.value} {variableUnit}
      </Typography>
      {xAxisType === "measuredAt" ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {dayjs(dataPoint.measuredAt).locale(i18n.language).format("LLL")}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block">
          {t("flow-tests.flow-test-measurement.testing-time")}: {dataPoint.testingTime}
        </Typography>
      )}
      {dataPoint.notes && (
        <Typography variant="caption" sx={{ mt: 0.5, display: "block", fontStyle: "italic" }}>
          <strong>{t("flow-tests.flow-test-measurement.notes")}:</strong> {dataPoint.notes}
        </Typography>
      )}
    </Paper>
  );
}

export default function FlowTestMeasurementsChart({
  measurementsData,
  variables,
  isChartMaximized,
  flowTestId,
  selectedVariableId,
  xAxisType,
}: Readonly<FlowTestMeasurementsChartProps>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);

  const filteredData = useMemo<ChartDataPoint[]>(() => {
    if (selectedVariableId === undefined) return [];

    const dataPoints = measurementsData
      .flatMap((group) => 
        group.measurements
          .filter((m) => m.variableId === selectedVariableId)
          .map((m) => ({
            id: m.id,
            value: m.value,
            measuredAt: group.measuredAt ? new Date(group.measuredAt) : null,
            testingTime: group.testingTime,
            flag: m.flag,
            notes: m.notes,
          }))
      )
      .filter((item) => {
        if (xAxisType === "measuredAt") return !!item.measuredAt;
        return item.testingTime !== null && item.testingTime !== undefined;
      })
      .sort((a, b) => {
        if (xAxisType === "measuredAt") {
          return (a.measuredAt?.getTime() ?? 0) - (b.measuredAt?.getTime() ?? 0);
        }
        return (a.testingTime ?? 0) - (b.testingTime ?? 0);
      });
      
    return dataPoints;
  }, [measurementsData, selectedVariableId, xAxisType]);

  const diffDays = useMemo(() => {
    if (xAxisType !== "measuredAt" || filteredData.length < 2) return 0;
    const first = filteredData[0].measuredAt!.getTime();
    const lastItem = filteredData.at(-1);
    if (!lastItem?.measuredAt) return 0;
    return (lastItem.measuredAt.getTime() - first) / (1000 * 60 * 60 * 24);
  }, [filteredData, xAxisType]);

  const selectedVariable = variables.find(v => v.id === selectedVariableId);
  const variableName = selectedVariable?.name ?? "Value";
  const variableUnit = selectedVariable?.unit;
  const label = variableUnit ? `${variableName} (${variableUnit})` : variableName;

  const height = isChartMaximized ? 600 : 350;

  const singleRecord = filteredData.length === 1;
  const singleValue = filteredData[0]?.value ?? 0;
  
  const handleExport = () => {
    const svg = chartRef.current?.querySelector("svg");
    if (svg) {
      const filename = `FlowTest_${flowTestId}_${sanitizeFilename(variableName)}`;
      exportSvg(svg, filename);
    }
  };

  if (variables.length === 0) return null;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Tooltip title={t("generic.export")}>
          <span>
            <IconButton onClick={handleExport} color="primary" disabled={!selectedVariableId || filteredData.length === 0}>
              <Download />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box ref={chartRef}>
        {selectedVariableId !== undefined && filteredData.length > 0 ? (
          <LineChart
            dataset={filteredData}
            xAxis={[{
              dataKey: xAxisType,
              scaleType: xAxisType === "measuredAt" ? "time" : "linear",
              valueFormatter: (value: any) => {
                if (xAxisType === "measuredAt") {
                  const d = dayjs(value).locale(i18n.language);
                  if (diffDays > 180) {
                    return d.month() === 0 ? d.format("MMM YY") : d.format("MMM");
                  }
                  if (diffDays > 14) return d.format("DD MMM");
                  if (diffDays > 1) {
                    return d.hour() === 0 ? d.format("DD MMM") : d.format("HH:mm");
                  }
                  return d.hour() === 0 && d.minute() === 0 ? d.format("DD MMM HH:mm") : d.format("HH:mm");
                }
                return value.toString();
              },
              label: xAxisType === "testingTime" ? t("flow-tests.flow-test-measurement.testing-time") : undefined,
            }]}
            yAxis={[{
              min: singleRecord ? singleValue - 1 : undefined,
              max: singleRecord ? singleValue + 1 : undefined,
              valueFormatter: (value: number) => (variableUnit ? `${value} ${variableUnit}` : `${value}`),
            }]}
            series={[{
              dataKey: "value",
              label: label,
              color: theme.palette.secondary.main,
              showMark: singleRecord,
            }]}
            slots={{
              axisContent: MeasurementAxisContent,
              noDataOverlay: NoDataOverlay,
            }}
            slotProps={{
              axisContent: {
                filteredData,
                variableUnit,
                xAxisType,
              } as MeasurementAxisContentProps,
              noDataOverlay: {
                message: t("flow-tests.flow-test-measurement.no-data"),
              } as NoDataOverlayProps,
            }}
            grid={{ vertical: true, horizontal: true }}
            height={height}
            skipAnimation
          />
        ) : (
          <Box sx={{ 
            height, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            opacity: 0.5,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1
          }}>
            <ShowChart sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6">
              {t("flow-tests.flow-test-measurement.select-variable-to-view-chart")}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
